import { type JSX, useState } from "react";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import Loader from "common/components/Loader";
import RenderIf from "common/components/RenderIf";
import SearchInput from "common/components/SearchInput";
import { Button } from "common/components/ui/Button";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useApiTokens from "../../datahooks/useApiTokens";
import CreateApiTokenButton from "./CreateApiTokenButton";
import DeleteTokenDialog from "./DeleteTokenDialog";
import DesktopTable from "./DesktopTable";
import EditNameDialog from "./EditNameDialog";
import MasterApiKey from "./MasterApiKey";
import MobileList from "./MobileLayout/MobileList";

import type { ApiToken } from "../../types";

import NoAPIKeysFound from "assets/images/empty-placeholders/api-keys.png";
import ResultNotFoundImage from "assets/images/empty-placeholders/result-not-found.png";

export default function ApiKeys() {
  const isDesktop = useTwBreakpoint("lg");
  const { tokens, isLoadingTokens, tokensError, refetchTokens } =
    useApiTokens();
  const [action, setAction] = useState<"edit" | "delete">();
  const [selectedToken, setSelectedToken] = useState<ApiToken>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { role } = useSelectedWorkspaceContext();

  let mainComponent: JSX.Element;
  if (tokensError) {
    mainComponent = (
      <ErrorPlaceholder
        onRetry={refetchTokens}
        errorMessage="Could not display API keys"
      />
    );
  } else if (isLoadingTokens) {
    mainComponent = <Loader />;
  } else if (!tokens?.length) {
    mainComponent = (
      <EmptyPlaceholder
        title="No API keys found"
        subtitle="Create an API key to connect Aimfox with your application"
        imageSrc={NoAPIKeysFound}
        actionComponent={<CreateApiTokenButton size="md" />}
      />
    );
  } else {
    const Component = isDesktop ? DesktopTable : MobileList;

    const filteredTokens = tokens.filter(({ name }) => {
      return name.toLowerCase().includes(searchTerm.toLowerCase());
    });

    mainComponent = filteredTokens.length ? (
      <Component
        tokens={filteredTokens}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    ) : (
      <EmptyPlaceholder
        title="No API keys found"
        subtitle="No API keys match your search criteria. Try adjusting your search"
        imageSrc={ResultNotFoundImage}
        actionComponent={
          <Button onClick={() => setSearchTerm("")}>Clear search</Button>
        }
      />
    );
  }

  function handleEdit(apiToken: ApiToken) {
    setSelectedToken(apiToken);
    setAction("edit");
  }

  function handleDelete(apiToken: ApiToken) {
    setSelectedToken(apiToken);
    setAction("delete");
  }

  return (
    <>
      <RenderIf condition={!!tokens?.length}>
        <SearchInput
          placeholder="Search API keys"
          value={searchTerm}
          className="lg:max-w-80"
          variant={isDesktop ? "sm" : "lg"}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClear={() => setSearchTerm("")}
        />
      </RenderIf>
      <RenderIf condition={role === "owner"}>
        <MasterApiKey />
      </RenderIf>
      {mainComponent}
      <RenderIf condition={!isLoadingTokens}>
        {/* Editing dialog */}
        <EditNameDialog
          apiToken={selectedToken}
          isOpen={action === "edit"}
          onOpenChange={() => setAction(undefined)}
        />
        {/* Deleting Dialog */}
        <DeleteTokenDialog
          apiToken={selectedToken}
          isOpen={action === "delete"}
          onOpenChange={() => setAction(undefined)}
        />
      </RenderIf>
    </>
  );
}
